import { DrillDownMenuItem } from "@/components/root/drilldown-menu/drilldown-menu.state";
import { UseModal } from "@/components/root/modal";
import { UseToast, ToastType } from "@/components/root/toast/toast.state";
import { EventBus } from "@/eventbus";
import { is_path_eq, isEmpty, uniqby } from "@/fp";
import { getEmailAddressFromExtendedData } from "@/gql/types";
import router from "@/router";
import JustificationModal from "@/views/customize/in-motion/justification/JustificationModal.vue";

export const ManageTrustMenuItem = (
  entities: Array<{
    id: string;
    name: string;
    icon: string;
    type: "person" | "organization";
  }>
): DrillDownMenuItem => {
  const entities_list = uniqby("id")(
    entities.map((item) => {
      return {
        id: item.id,
        name: item.name,
        //  email: item.email,
        disable: false,
        isCategory: false,
        isPremium: false,
        subItems: [],
        icon: item.icon,
        action: "manage_" + item.id + "_" + item.type,
      };
    })
  );
  return {
    disable: false,
    isCategory: true,
    isPremium: false,
    icon: "Trust",
    name: "Manage Trust",
    subItems: [
      {
        isCategory: false,
        isList: true,
        isPremium: false,
        icon: "",
        name: "",
        disable: false,
        subItems: entities_list,
      },
    ],
  };
};

export const ManageEntityTrustMenuItem = (id: string): DrillDownMenuItem => ({
  disable: false,
  isCategory: false,
  isPremium: false,
  icon: "Trust",
  name: "Manage Trust",
  action: "manage_" + id,
  subItems: [],
});

export const InvestigateSingleEntity = (icon: string) => (
  name: string
): DrillDownMenuItem => {
  return {
    disable: false,
    isCategory: true,
    isPremium: false,
    icon: "Investigation",
    name: "Investigate",
    subItems: [
      {
        disable: false,
        isCategory: false,
        isPremium: false,
        action: "investigate_entity",
        icon,
        name,
        subItems: [],
      },
    ],
  };
};

const should_nav_to_event_justification_page = is_path_eq(
  "msg.action",
  "event_justification_page"
);

export const AskForEventJustification: [
  string,
  DrillDownMenuItem,
  (share_event: any) => Promise<void>
] = [
  "justify",
  {
    disable: false,
    isCategory: false,
    action: "justify",
    icon: "Email",
    name: "Ask For Justification",
    isPremium: false,
    subItems: [],
  },
  async (share_event) => {
    const { eventType, source, date, id, items, exposedTo } = share_event;
    let { exposedBy } = share_event;
    if (!exposedBy) {
      // in case of FileDownloaded exposedBy will be empty!
      // so the person we need is in the exposedTo array
      exposedBy = exposedTo.length > 0 ? exposedTo[0] : { name: "", email: "" };
    }
    const { name, email } = exposedBy;
    console.log("share_event :", share_event);
    const res = await UseModal().show(JustificationModal, {
      source,
      id,
      files: items.map((d: any) => d.name),
      date,
      email,
      name,
      eventType,
      exposedTo: exposedTo.map((d: any) => d.name),
    });

    if (res.type === "success") {
      EventBus.emit("new-justification", {
        event_id: id,
        // @ts-ignore
        justification_id: res.msg.id!,
      });

      if (should_nav_to_event_justification_page(res)) {
        router().push({ name: "Reports", query: { tab: "events" } });
      }
    }
  },
];

export const customLabelMenu = (isPremium: boolean): DrillDownMenuItem => {
  return {
    name: "Customize information type",
    icon: "Label",
    isCategory: true,
    isPremium: isPremium,
    premiumKey: "customInfoTypes",
    disable: false,
    subItems: [
      {
        name: "Assign existing",
        icon: "Label",
        disable: false,
        action: "assign-existing",
        isCategory: false,
        subItems: [],
        isPremium: false,
      },
      {
        name: "Add new",
        icon: "Add",
        disable: false,
        action: "add-new",
        isCategory: false,
        subItems: [],
        isPremium: false,
      },
      {
        borderTop: true,
        name: "Manage",
        icon: "Manage",
        disable: false,
        action: "manage-labels",
        isCategory: false,
        subItems: [],
        isPremium: false,
      },
    ],
  };
};

export interface EventMenuInput {
  senderName: string;
  files: any[];
  exposedTo: any[];
}

export const InvestigateEvent = (input: EventMenuInput): DrillDownMenuItem => {
  const exposedToCount = input.exposedTo.filter((s) => s.externalId || s.name)
    .length;
  return {
    disable: false,
    isCategory: true,
    isPremium: false,
    icon: "Investigation",
    name: "Investigate",
    subItems: [
      {
        disable: isEmpty(input.senderName),
        isCategory: true,
        isPremium: false,
        icon: "ExposedBy",
        name: "Exposed by",
        subItems: [
          {
            disable: false,
            isCategory: false,
            isPremium: false,
            action: "investigate_sender",
            name: input.senderName,
            subItems: [],
          },
        ],
      },

      {
        disable: exposedToCount === 0,
        isCategory: true,
        isPremium: false,
        icon: "ExposedTo",
        name: "Exposed to",
        subItems: [
          {
            isList: true,
            disable: false,
            isCategory: false,
            isPremium: false,
            name: "",
            subItems: input.exposedTo
              .map((s, index) => {
                return {
                  subItems: [],
                  action: "investigate_exposedTo_" + s.id,
                  name: getEmailAddressFromExtendedData(s) || s.name,
                  isPremium: false,
                  isCategory: false,
                  disable: false,
                };
              })
              .filter((f) => f.name),
          },
        ],
      },
      {
        disable: false,
        isCategory: true,
        isPremium: false,
        icon: "File",
        name: "Files",
        subItems: [
          {
            isList: true,
            disable: false,
            isCategory: false,
            isPremium: false,
            name: "",
            subItems: input.files
              .map((s, index) => ({
                subItems: [],
                action: "investigate_file_" + s.id,
                name: s.name,
                icon: "File",
                isPremium: false,
                isCategory: false,
                disable: false,
              }))
              .filter((f) => f.name),
          },
        ],
      },
    ].filter((f) => !f.disable),
  };
};
